import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { capitalize, orderBy } from 'lodash';
import { renderBooleanValue } from '../../../../../util/miscellaneousUtils';
import { Progress, Modal, Tooltip, Checkbox, Spin } from 'antd';
import { LockFilled } from '@ant-design/icons';
import {
  RadioButttonWithIcons,
  getAlertRadioOptions
} from '../../../../../components/ns/RadioButttonWithIcons';
import styles from './index.scss';
import { isResourceAvailable } from '../../../../../util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentAltLines,
  faEllipsisH
} from '@fortawesome/pro-regular-svg-icons';

const resourceCode = 'amls:ui:common-alerts:read';

export const NSAlertDetailHits = ({
  GS,
  data,
  apiActions,
  authentication,
  totalDiff,
  selectedForCompare,
  onSelectForComparision,
  hitsUserFeedback,
  alertLevels,
  individualCorporateType = 'I'
}) => {
  const [isModalVisible, setModalVibility] = useState(false);
  const [selectedHitId, setSelectedHitId] = useState(null);
  const setHitId = (hitId) => {
    setSelectedHitId(hitId);
    setModalVibility(true);
  };

  const isCorporate = individualCorporateType === 'C';
  const columnsToRemove = isCorporate
    ? ['Identification Number', 'Identification Number Match']
    : ['Registered Number', 'Registered Number Match'];

  GS = {
    ...GS,
    widgetConfig: GS?.widgetConfig?.filter(
      ({ columnName }) => !columnsToRemove.includes(columnName)
    )
  };

  const renderDataByMultipleKey = (
    column,
    record,
    elementType = '',
    fromData = null
  ) => {
    let keys = column.itemName.split('|');
    const dataRecord = { ...record };
    if (keys.length >= 2 && record !== undefined) {
      let renderCount = 0;
      return keys.map((key, index) => {
        if (
          (key == 'partyIdNo' || key == 'registeredNumber') &&
          fromData === 'hitsData'
        ) {
          dataRecord[key] = null;
        }
        if (key == 'partyKey' && fromData === 'hitsData') {
          dataRecord[key] = null;
        }
        if (dataRecord[key]) renderCount = 1;
        if (renderCount === 1) {
          return elementType === 'TD' ? (
            <td key={index}>
              {dataRecord[key] ? <p>{dataRecord[key]}</p> : null}
            </td>
          ) : (
            <>
              {key == 'watchlistIdentificationNumber' && dataRecord[key] ? (
                <p key={index} id={fromData} className={styles.lockedField}>
                  <span>
                    {dataRecord[key] !== null && dataRecord[key] !== 'null'
                      ? dataRecord[key]
                      : '--'}
                  </span>
                  {dataRecord['isHardRule'] && (
                    <LockFilled className={styles.lockIcon} />
                  )}
                </p>
              ) : (
                <p key={index} id={fromData}>
                  {dataRecord[key] ? dataRecord[key] : null}
                </p>
              )}
            </>
          );
        } else if (renderCount === 0 && index === keys.length - 1)
          return elementType === 'TD' ? (
            <td key={index}>
              {dataRecord[key] ? <p>{dataRecord[key]}</p> : '--'}
            </td>
          ) : (
            <p key={index}>{dataRecord[key] ? dataRecord[key] : '--'}</p>
          );
      });
    } else return null;
  };

  const onUserFeedbackChangeHandler = (obj, record) => {
    const param = {
      alertId: data.alertId,
      alertSourceId: data.alertSourceId,
      watchlistId: record.watchlistId,
      newAlertStatus: obj,
      hitId: record.hitId
    };
    apiActions.postNSHitsUserFeedback(param);
  };

  const addToCompare = (hitId) => {
    onSelectForComparision(hitId);
  };

  const renderValue = useCallback((value) => {
    const urlRegex = /https?:\/\/[^\s]+/;
    const urlSplitRegex = /(https?:\/\/[^\s]+)/g;

    if (!urlRegex.test(value)) {
      if (typeof value === 'string') return value;
      return (
        <React.Fragment key={value}>
          {JSON.stringify(value, null, 2)}
        </React.Fragment>
      );
    }

    const parts = value.split(urlSplitRegex).filter((part) => part.length > 0);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            // The href attribute needs to be a valid URL, but it's possible that the URL
            // in the alert data has a trailing semicolon (e.g. "https://example.com/;").
            // If that's the case, we need to remove the semicolon so that the href attribute
            // is a valid URL. Otherwise, the link won't work.
            href={part.replace(/[;,.\)]*$/, '')} //Trims unwanted chars
            target="_blank"
            rel="noopener noreferrer"
            style={{ textTransform: 'none' }}
            key={index}>
            {part}
          </a>
        );
      }
      return part;
    });
  }, []);

  const renderArray = useCallback(
    (value) => {
      return value.map((item, index) => {
        return (
          <React.Fragment key={index}>
            {renderValue(item)}
            {index !== value.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      });
    },
    [renderValue]
  );

  const renderCustomTableColumn = (column, record) => {
    const { isWhitelisted: isWhitelistedGlobally = false } = data;
    switch (column?.itemName) {
      case 'isWhitelisted':
        return (
          <td key={`v-${record.alertId}`}>
            {isWhitelistedGlobally == true
              ? ' True (Global) '
              : renderBooleanValue(record[column?.itemName])}
          </td>
        );
      case 'reviewFeedback':
        return (
          <td key={record.hitId}>
            {isResourceAvailable(authentication, resourceCode) ? (
              <Spin
                wrapperClassName={styles.hitSpin}
                spinning={
                  hitsUserFeedback?.isLoading &&
                  hitsUserFeedback?.hitIds?.includes(record.hitId)
                }>
                <RadioButttonWithIcons
                  options={getAlertRadioOptions(alertLevels)}
                  defaultValue={record[column.itemName]?.toString()}
                  onChangeHandler={(res) =>
                    onUserFeedbackChangeHandler(res, record)
                  }
                />
              </Spin>
            ) : (
              '--'
            )}
          </td>
        );
      case 'hitScore':
        return (
          <>
            <div className={styles.flex} key={record.alertId}>
              <div>
                <span>
                  {record.hitLevelLabel || capitalize(record.hitLevel)}
                </span>
                <br />
                <Tooltip title={record[column.itemName]}>
                  <span>{record[column.itemName]?.toFixed(2).toString()}</span>
                </Tooltip>
                {record.hitScore && (
                  <Progress
                    percent={record.hitScore}
                    strokeColor={record.hitLevelColor}
                    showInfo={false}
                  />
                )}
              </div>
            </div>
            <Checkbox
              style={{ marginRight: '15px' }}
              checked={selectedForCompare.some((s) => s === record.hitId)}
              onChange={() => addToCompare(record.hitId)}
            />
          </>
        );
      case 'furtherInformation':
        return (
          <td key={record.alertId}>
            {record.furtherInformation ? (
              <div className={styles.furtherInformation} key={record.hitId}>
                <div>
                  <FontAwesomeIcon
                    icon={faEllipsisH}
                    style={{ fontSize: '11px', marginRight: '6px' }}
                  />
                  <FontAwesomeIcon
                    icon={faCommentAltLines}
                    style={{ fontSize: '20px' }}
                    onClick={() => {
                      setHitId(record.hitId);
                    }}
                  />
                </div>
                {record.furtherInformation && selectedHitId == record.hitId && (
                  <Modal
                    wrapClassName={styles.alertModalClass}
                    title="Further Information"
                    visible={isModalVisible}
                    width="735px"
                    footer={null}
                    onCancel={() => {
                      setModalVibility(false);
                    }}>
                    {typeof record.furtherInformation === 'string' ? (
                      // Attempt to parse furtherInformation as JSON
                      (() => {
                        try {
                          const obj = JSON.parse(record.furtherInformation);
                          // If parsing is successful, render key-value pairs
                          return (
                            <div style={{ display: 'flex' }}>
                              <div>
                                {Object.entries(obj).map(([key, value]) => (
                                  <div
                                    key={key}
                                    style={{
                                      marginBottom: '12px'
                                    }}>
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        marginBottom: '8px',
                                        textDecoration: 'underline',
                                        textTransform: 'capitalize',
                                        marginRight: '8px'
                                      }}>
                                      {key}:
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '14px',
                                        textTransform: 'capitalize',
                                        wordBreak: 'break-all',
                                        lineHeight: '1.2'
                                      }}>
                                      {(Array.isArray(value)
                                        ? renderArray(value)
                                        : renderValue(value)) || '-'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        } catch (error) {
                          // If JSON parsing fails, render the string as is
                          return <div>{record.furtherInformation}</div>;
                        }
                      })()
                    ) : (
                      // Render string value if furtherInformation is not a string
                      <div>{record.furtherInformation}</div>
                    )}
                  </Modal>
                )}
              </div>
            ) : (
              '--'
            )}
          </td>
        );
    }
    return null;
  };

  const getNestedTableColumns = (
    columnDetail,
    rowData,
    TYPE,
    fromData = null
  ) => {
    return orderBy(columnDetail, ['itemIndex'], ['asc'])
      .filter((column) => column.visibility)
      .map((column, idx) => {
        const columnKey = `${column.itemName}-${idx}`;

        if (TYPE === 'HEADING') {
          return <p key={columnKey}>{column.itemLabel}</p>;
        }

        if (
          (column.itemName === 'hitLevel' || column.itemName === 'hitScore') &&
          rowData.hits !== undefined
        ) {
          return null;
        }

        if (
          renderCustomTableColumn(column, rowData) ||
          column.itemName === 'hitLevel'
        ) {
          return (
            <React.Fragment key={columnKey}>
              {renderCustomTableColumn(column, rowData)}
            </React.Fragment>
          );
        }

        if (renderDataByMultipleKey(column, rowData)) {
          return (
            <React.Fragment key={columnKey}>
              {renderDataByMultipleKey(column, rowData, TYPE, fromData)}
            </React.Fragment>
          );
        }
        const columnValue = rowData[column.itemName];
        // Corner case handling for null/undefined, 0, empty string, or invalid data
        const isValidNumber =
          typeof columnValue === 'number' && !isNaN(columnValue);
        const displayValue =
          columnValue === 0
            ? '0.00'
            : isValidNumber
            ? columnValue.toFixed(2)
            : columnValue;

        const isScoreOrWeight =
          column.itemName.toLowerCase().includes('score') ||
          column.itemName.toLowerCase().includes('weight');
        const tooltipTitle =
          isScoreOrWeight && columnValue !== undefined
            ? isValidNumber
              ? columnValue.toString()
              : columnValue
            : '--';
        if (isScoreOrWeight) {
          return (
            <Tooltip key={columnKey} title={tooltipTitle}>
              <p
                key={columnKey}
                style={{
                  color:
                    (rowData[column.itemName] == 100 ||
                      rowData[column.itemName] == 1) &&
                    column.itemName.toLowerCase().includes('score')
                      ? '#DF6057'
                      : '#556069',
                  ...(columnDetail?.length > 2 && { flexBasis: '46px' }),
                  textAlign: 'start',
                  wordBreak: 'break-all',
                  cursor: 'pointer'
                }}>
                {columnValue || columnValue === 0 ? displayValue : '--'}
              </p>
            </Tooltip>
          );
        } else {
          return (
            <p
              key={columnKey}
              style={{
                maxWidth: '22ch',
                wordBreak: 'break-all'
              }}>
              {columnValue ?? '--'}
            </p>
          );
        }
      });
  };

  const getAlertViewHitsHeading = (GS) => {
    let renderHeading = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    renderHeading = sortedSettings.map((setting, id) => {
      return (
        <React.Fragment key={id}>
          {setting.columnDetail.length === 0 ? (
            <div className={styles.separatorBar}>{setting.columnName}</div>
          ) : (
            <tr>
              {setting.columnDetail.length >= 2 ? (
                <React.Fragment>
                  <th className={styles.spaceBetween} key={id}>
                    {getNestedTableColumns(
                      setting.columnDetail,
                      undefined,
                      'HEADING'
                    )}
                  </th>
                  <th className={styles.spaceBetween} key={id + 1}>
                    {getNestedTableColumns(setting.columnDetail, data, 'DATA')}
                  </th>
                </React.Fragment>
              ) : (
                orderBy(setting.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column, idx) => {
                    return (
                      <React.Fragment key={idx}>
                        <th className={styles.bold}> {column.itemLabel} </th>
                        <th>
                          {renderDataByMultipleKey(column, data)
                            ? renderDataByMultipleKey(column, data)
                            : data[column.itemName]
                            ? data[column.itemName]
                            : column.itemName === 'reviewFeedback'
                            ? ''
                            : '--'}
                        </th>
                      </React.Fragment>
                    );
                  })
              )}
            </tr>
          )}
        </React.Fragment>
      );
    });

    return renderHeading;
  };

  const getAlertViewHitsData = (GS) => {
    let renderData = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    renderData = data?.hits?.map((item, itemId) => {
      return (
        <div
          key={itemId}
          className={`${
            selectedForCompare.some((s) => s === item.hitId)
              ? styles.selected
              : ''
          }`}>
          {sortedSettings.map((setting, id) => {
            const isDiffValue = totalDiff.some(
              (s) =>
                setting.columnDetail.some((c) =>
                  c.itemName.split('|').includes(s)
                ) && selectedForCompare.some((s) => s === item.hitId)
            );
            return (
              <tr
                key={id}
                className={`${isDiffValue ? 'diff' : ''} ${
                  setting.columnDetail.length === 0 ? styles.seperator : ''
                }`}>
                {orderBy(setting?.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column, idx) => {
                    if (setting.columnDetail.length >= 2) {
                      if (column.itemIndex === 0)
                        return (
                          <td className={styles.spaceBetween} key={idx}>
                            {getNestedTableColumns(
                              setting.columnDetail,
                              item,
                              'DATA',
                              'hitsData'
                            )}
                          </td>
                        );
                      else return null;
                    } else if (renderCustomTableColumn(column, item) !== null)
                      return renderCustomTableColumn(column, item);
                    else if (
                      renderDataByMultipleKey(column, item, 'TD') !== null
                    )
                      return renderDataByMultipleKey(column, item, 'TD');
                    else {
                      const { itemName } = column || {};
                      const displayKey =
                        itemName === 'partyIdNo' ||
                        itemName === 'registeredNumber'
                          ? 'watchlistIdentificationNumber'
                          : itemName;

                      const value = item?.[displayKey] || '--';

                      return (
                        <td key={idx}>
                          <p>{value}</p>
                        </td>
                      );
                    }
                  })}
              </tr>
            );
          })}
        </div>
      );
    });

    return renderData;
  };

  if (GS?.isVisible)
    return (
      <React.Fragment>
        <div className={styles.partyIdentity}>
          <table className={styles.hitsTable}>
            <thead>{getAlertViewHitsHeading(GS)}</thead>
            <tbody>{getAlertViewHitsData(GS)}</tbody>
          </table>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
            <p className={styles.hints}>
              <b>*</b> These fields are only applicable for individuals
            </p>
            <p
              className={styles.hints}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span role="img" aria-label="lock" style={{ marginTop: '-6px' }}>
                <LockFilled style={{ color: '#1890ff', fontSize: '10px' }} />
              </span>
              <span>Hard Rule Parameter</span>
            </p>
          </div>
        </div>
      </React.Fragment>
    );
  else return null;
};

NSAlertDetailHits.propTypes = {
  GS: PropTypes.object,
  data: PropTypes.object,
  apiActions: PropTypes.object,
  hitsUserFeedback: PropTypes.object,
  onSelectForComparision: PropTypes.func,
  alertLevels: PropTypes.array
};

export default NSAlertDetailHits;
